import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import { infantStories, donationImpacts } from "@/mocks/infantDonations";

const InfantDonationPage = () => {
  const [selectedInfant, setSelectedInfant] = useState<string | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"needs" | "funded">("needs");
  const navigate = useNavigate();

  const needsInfants = infantStories.filter((i) => !i.funded);
  const fundedInfants = infantStories.filter((i) => i.funded);

  const handleDonate = (infantId: string) => {
    setSelectedInfant(infantId);
    setShowForm(true);
    setTimeout(() => {
      document.getElementById("donation-form-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    if (selectedInfant) body.append("infant_id", selectedInfant);
    const infant = infantStories.find((i) => i.id === selectedInfant);
    if (infant) body.append("infant_name", infant.infantName);
    body.append("donation_amount", String(selectedAmount || customAmount));
    setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d7ekvlb5bqmofchk7j80", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedInfantData = infantStories.find((i) => i.id === selectedInfant);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Donate to Infants – Give Milk, Warmth & Love | SuperMiddleEast"
        description="Support infants around the world with a donation. Lottery winners and generous donors welcome. Receive a handmade gift, photo and official receipt from the baby's family. 100% transparent."
        keywords="infant donation, baby charity, donate to infants, lottery winner donation, charity Middle East, baby support fund"
        canonical={`${BASE_URL}/infant-donation`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Donate to Infants – SuperMiddleEast",
            "description": "Support infants around the world. Receive a handmade gift and photo from the baby's family. 100% transparent donations.",
            "url": `${BASE_URL}/infant-donation`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "Infant Donation", "item": `${BASE_URL}/infant-donation` },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "DonateAction",
            "name": "Donate to Infants",
            "description": "Donate to support infants around the world. Receive a handmade gift and photo from the baby's family.",
            "url": `${BASE_URL}/infant-donation`,
            "recipient": {
              "@type": "Organization",
              "name": "SuperMiddleEast Infant Donation Program",
              "url": `${BASE_URL}/infant-donation`,
            },
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/1320a641bffcef13004950b7b677c356.jpg"
          alt="Infants around the world"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-rose-900/70 via-rose-800/60 to-rose-950/85"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-24 pb-16">
          <div className="inline-flex items-center gap-2 bg-rose-400/20 border border-rose-300/50 text-rose-100 text-xs font-semibold px-5 py-2 rounded-full mb-6 backdrop-blur-sm">
            <i className="ri-heart-fill text-rose-300"></i>
            Lottery Winners &amp; Generous Hearts Welcome
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Give Love, Milk &amp;
            <br />
            <span className="text-rose-300">Warmth to Infants</span>
          </h1>
          <p className="text-rose-100 text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
            A small donation from a lottery winner or generous soul can change an infant&apos;s life forever.
            <strong className="text-white"> You receive a heartfelt gift, receipt, and a photo</strong> from the baby&apos;s family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => document.getElementById("infants-section")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-rose-500 hover:bg-rose-400 text-white font-bold px-8 py-4 rounded-2xl transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center text-base"
            >
              <i className="ri-heart-fill"></i>
              Choose an Infant to Support
            </button>
            <button
              onClick={() => document.getElementById("how-it-works-section")?.scrollIntoView({ behavior: "smooth" })}
              className="border-2 border-white/50 text-white font-bold px-8 py-4 rounded-2xl hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center text-base"
            >
              <i className="ri-question-line"></i>
              How It Works
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-rose-50 border-b border-rose-100 py-8">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "1,240+", label: "Infants Supported", icon: "ri-heart-fill", color: "text-rose-500" },
            { value: "68", label: "Countries Reached", icon: "ri-global-line", color: "text-rose-500" },
            { value: "100%", label: "Gifts Delivered", icon: "ri-gift-fill", color: "text-amber-500" },
            { value: "3,800+", label: "Generous Donors", icon: "ri-user-heart-line", color: "text-rose-500" },
          ].map((s) => (
            <div key={s.label}>
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full mx-auto mb-2 border border-rose-100">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div className="text-2xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
              <div className="text-gray-500 text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <section id="how-it-works-section" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-3">Simple &amp; Transparent</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              How Your Donation Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", icon: "ri-heart-3-line", title: "Choose an Infant", desc: "Browse babies who need milk, clothes, or nutrition. Pick the one that touches your heart.", color: "bg-rose-50 border-rose-200" },
              { step: "2", icon: "ri-secure-payment-line", title: "Make a Donation", desc: "Choose your amount — even $25 provides a month of formula milk. Every dollar counts.", color: "bg-amber-50 border-amber-200" },
              { step: "3", icon: "ri-gift-2-line", title: "Receive a Gift", desc: "The infant's family sends you a handmade gift — a bracelet, painting, or craft from their culture.", color: "bg-rose-50 border-rose-200" },
              { step: "4", icon: "ri-image-2-line", title: "Get Photo & Receipt", desc: "You receive an official receipt plus a real photo of the baby with your gift — proof your love arrived.", color: "bg-amber-50 border-amber-200" },
            ].map((item) => (
              <div key={item.step} className={`rounded-2xl border p-6 text-center ${item.color}`}>
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full mx-auto mb-4 border border-rose-100">
                  <i className={`${item.icon} text-rose-500 text-xl`}></i>
                </div>
                <div className="text-xs font-black text-rose-400 mb-2">STEP {item.step}</div>
                <div className="font-black text-gray-900 text-base mb-2">{item.title}</div>
                <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Impact */}
      <section className="py-16 bg-rose-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              What Your Donation Provides
            </h2>
            <p className="text-gray-500 text-sm mt-2">Every amount makes a real difference</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {donationImpacts.map((impact) => (
              <div key={impact.amount} className="bg-white rounded-2xl border border-rose-100 p-5 flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-rose-50 rounded-xl flex-shrink-0">
                  <i className={`${impact.icon} text-rose-500 text-2xl`}></i>
                </div>
                <div>
                  <div className="text-2xl font-black text-rose-600" style={{ fontFamily: "'Playfair Display', serif" }}>${impact.amount}</div>
                  <div className="text-gray-600 text-sm font-medium">{impact.label}</div>
                </div>
              </div>
            ))}
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-vip-diamond-fill text-white text-2xl"></i>
              </div>
              <div>
                <div className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Custom</div>
                <div className="text-rose-100 text-sm font-medium">Your generous amount</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infants Section */}
      <section id="infants-section" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-3">Real Babies, Real Needs</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Infants Waiting for Your Love
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">Each baby has a real story. Your donation goes directly to their family with full transparency.</p>
          </div>

          {/* Tabs */}
          <div className="flex bg-rose-50 border border-rose-100 rounded-xl p-1 gap-1 w-fit mx-auto mb-8">
            {(["needs", "funded"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab ? "bg-rose-500 text-white" : "text-rose-500 hover:text-rose-600"
                }`}
              >
                {tab === "needs" ? `Needs Support (${needsInfants.length})` : `Fully Funded (${fundedInfants.length})`}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeTab === "needs" ? needsInfants : fundedInfants).map((infant) => (
              <div key={infant.id} className="bg-white border border-rose-100 rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-300">
                <div className="relative h-52 overflow-hidden">
                  <img src={infant.image} alt={infant.infantName} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="bg-white/90 text-rose-600 text-xs font-bold px-2.5 py-1 rounded-full">{infant.flag} {infant.country}</span>
                    <span className="bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{infant.need}</span>
                  </div>
                  {infant.funded && (
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill"></i> Funded
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3">
                    <div className="text-white font-black text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>{infant.infantName}</div>
                    <div className="text-white/80 text-xs">{infant.age} old</div>
                  </div>
                </div>

                <div className="p-5">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">{infant.story}</p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span className="font-semibold text-rose-600">${infant.amountRaised} raised</span>
                      <span>Goal: ${infant.amountNeeded}</span>
                    </div>
                    <div className="h-2 bg-rose-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${infant.funded ? "bg-emerald-500" : "bg-rose-500"}`}
                        style={{ width: `${Math.min(100, (infant.amountRaised / infant.amountNeeded) * 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Gift from family */}
                  {infant.giftReceived && (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4 flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                        <i className="ri-gift-fill text-amber-600 text-sm"></i>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-amber-700 mb-0.5">Gift from Family</div>
                        <div className="text-xs text-amber-600">{infant.giftReceived}</div>
                        {infant.donorName && (
                          <div className="text-xs text-gray-400 mt-1">Donated by {infant.donorName} · {infant.donorCountry}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {!infant.funded ? (
                    <button
                      onClick={() => handleDonate(infant.id)}
                      className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="ri-heart-fill"></i>
                      Donate to {infant.infantName}
                    </button>
                  ) : (
                    <div className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                      <i className="ri-checkbox-circle-fill"></i>
                      Fully Funded — Thank You!
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section id="donation-form-section" className="py-20 bg-rose-50">
        <div className="max-w-2xl mx-auto px-4">
          {submitted ? (
            <div className="bg-white rounded-3xl border border-rose-100 p-10 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-6">
                <i className="ri-heart-fill text-rose-500 text-4xl"></i>
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Thank You, Generous Soul!
              </h3>
              <p className="text-gray-600 text-base mb-4 leading-relaxed">
                Your donation has been received. You will get an <strong>official receipt</strong> by email, and the infant&apos;s family will send you a <strong>handmade gift + photo</strong> within 4–6 weeks.
              </p>
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-6 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-gift-fill text-amber-500"></i>
                  <span className="font-bold text-amber-700 text-sm">What happens next:</span>
                </div>
                <ul className="text-sm text-gray-600 space-y-1.5">
                  <li className="flex items-center gap-2"><i className="ri-mail-line text-rose-400"></i> Receipt sent to your email</li>
                  <li className="flex items-center gap-2"><i className="ri-image-2-line text-rose-400"></i> Baby photo with your gift — 2–4 weeks</li>
                  <li className="flex items-center gap-2"><i className="ri-gift-2-line text-amber-400"></i> Handmade family gift — 4–6 weeks</li>
                </ul>
              </div>
              <button
                onClick={() => { setSubmitted(false); setShowForm(false); setSelectedInfant(null); navigate("/infant-donation"); }}
                className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-8 py-3 rounded-xl cursor-pointer transition-colors"
              >
                Support Another Infant
              </button>
            </div>
          ) : showForm && selectedInfantData ? (
            <div className="bg-white rounded-3xl border border-rose-100 p-8">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-rose-50">
                <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0">
                  <img src={selectedInfantData.image} alt={selectedInfantData.infantName} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-xs text-rose-500 font-bold uppercase tracking-wide mb-1">Donating to</div>
                  <div className="font-black text-gray-900 text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>{selectedInfantData.infantName}</div>
                  <div className="text-gray-500 text-sm">{selectedInfantData.flag} {selectedInfantData.country} · {selectedInfantData.age} old</div>
                </div>
              </div>

              <h3 className="text-xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Complete Your Donation
              </h3>

              {/* Amount Selection */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-3">Choose Donation Amount (USD)</label>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[25, 50, 100, 250, 500, 1000].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(""); }}
                      className={`py-3 rounded-xl font-bold text-sm transition-all cursor-pointer border-2 ${
                        selectedAmount === amt
                          ? "bg-rose-500 border-rose-500 text-white"
                          : "border-rose-100 text-gray-700 hover:border-rose-300"
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                  <input
                    type="number"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                    className="w-full pl-8 pr-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400"
                    min="1"
                  />
                </div>
              </div>

              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Your Full Name *</label>
                    <input name="full_name" type="text" required placeholder="e.g. John Smith" className="w-full px-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Address *</label>
                    <input name="email" type="email" required placeholder="your@email.com" className="w-full px-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Your Country *</label>
                    <input name="country" type="text" required placeholder="e.g. United Arab Emirates" className="w-full px-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Phone (for gift delivery)</label>
                    <input name="phone" type="tel" placeholder="+1 234 567 8900" className="w-full px-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Shipping Address (for family gift) *</label>
                  <textarea name="shipping_address" required placeholder="Full address where you want to receive the family gift and photo..." rows={3} maxLength={500} className="w-full px-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400 resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Message to the Family (optional)</label>
                  <textarea name="message_to_family" placeholder="Write a loving message to the infant's family..." rows={3} maxLength={500} className="w-full px-4 py-3 border-2 border-rose-100 rounded-xl text-sm focus:outline-none focus:border-rose-400 resize-none" />
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-gift-fill text-amber-500 mt-0.5"></i>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    <strong>Your gift from the family:</strong> After your donation is processed, the infant&apos;s family will handcraft a special gift from their culture and send it to your address along with a photo of the baby. You will also receive an official donation receipt by email.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting || (!selectedAmount && !customAmount)}
                  className="w-full bg-rose-500 hover:bg-rose-600 disabled:bg-rose-300 text-white font-black py-4 rounded-2xl transition-colors cursor-pointer flex items-center justify-center gap-2 text-base"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Processing...</>
                  ) : (
                    <><i className="ri-heart-fill"></i> Donate ${selectedAmount || customAmount || "..."} to {selectedInfantData.infantName}</>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400">100% of your donation goes directly to the infant&apos;s family. Fully transparent.</p>
              </form>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-4">
                <i className="ri-heart-fill text-rose-400 text-4xl"></i>
              </div>
              <h3 className="text-xl font-black text-gray-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Choose an Infant Above</h3>
              <p className="text-gray-400 text-sm">Click &quot;Donate&quot; on any infant card to start your donation</p>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials from donors */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              What Donors Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "Mohammed Al-Rashid",
                country: "🇦🇪 UAE",
                text: "I won a lottery and wanted to share the blessing. I donated to baby Kofi and received the most beautiful handwoven bracelet. The photo of him smiling made me cry happy tears.",
                gift: "Kente bracelet from Ghana",
                avatar: "https://readdy.ai/api/search-image?query=professional%20Arab%20businessman%20portrait%20warm%20smile%20confident%20UAE%20Middle%20East%20white%20background&width=80&height=80&seq=don1&orientation=squarish",
              },
              {
                name: "Sarah Mitchell",
                country: "🇬🇧 United Kingdom",
                text: "The receipt arrived same day, and 5 weeks later I got a tiny painted tile from baby Miguel's family in Haiti. It sits on my desk and reminds me every day that kindness travels far.",
                gift: "Painted tile from Haiti",
                avatar: "https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20warm%20smile%20confident%20UK%20British%20white%20background&width=80&height=80&seq=don2&orientation=squarish",
              },
              {
                name: "Chen Wei",
                country: "🇨🇳 China",
                text: "I donated $500 for a full year of support. The family sent me a handmade embroidered cloth and a photo album of the baby growing month by month. Absolutely priceless.",
                gift: "Embroidered cloth + photo album",
                avatar: "https://readdy.ai/api/search-image?query=professional%20Chinese%20businessman%20portrait%20warm%20smile%20confident%20white%20background&width=80&height=80&seq=don3&orientation=squarish",
              },
            ].map((t) => (
              <div key={t.name} className="bg-rose-50 border border-rose-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-rose-200">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{t.name}</div>
                    <div className="text-gray-400 text-xs">{t.country}</div>
                  </div>
                  <div className="ms-auto flex gap-0.5">
                    {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-amber-400 text-xs"></i>)}
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-3 italic">&quot;{t.text}&quot;</p>
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <i className="ri-gift-fill text-amber-500 text-xs"></i>
                  <span className="text-xs text-amber-700 font-medium">{t.gift}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rose-500 to-rose-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <div className="text-5xl mb-4">👶</div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Luck Can Be Their Life
          </h2>
          <p className="text-rose-100 text-lg mb-8 leading-relaxed">
            Lottery winners, successful entrepreneurs, and generous hearts — a small share of your fortune gives an infant a healthy start. They will remember you forever.
          </p>
          <button
            onClick={() => document.getElementById("infants-section")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-white text-rose-600 font-black px-10 py-4 rounded-2xl hover:bg-rose-50 transition-colors cursor-pointer text-lg flex items-center gap-3 mx-auto"
          >
            <i className="ri-heart-fill text-rose-500"></i>
            Choose an Infant to Support
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InfantDonationPage;
